/* eslint-disable id-length */
import Icon from "app/components/shared/Icon";
import { LogControl } from "app/components/shared/Log";
import debounce from "lodash/debounce";
import {
  createContext,
  KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

interface LogSearchContext {
  search: string;
  position: number;
  totalMatches: number;

  setSearch: (search: string) => void;
  setPosition: (position: number) => void;
  setTotalMatches: (totalMatches: number) => void;
}

export const LogSearchContext = createContext<LogSearchContext>({
  search: "",
  position: 0,
  totalMatches: 0,
  setSearch: () => {
    return;
  },
  setPosition: () => {
    return;
  },
  setTotalMatches: () => {
    return;
  },
});

export const LogSearchProvider = ({ children }) => {
  const [search, setSearch] = useState("");
  const [position, setPosition] = useState(0);
  const [matches, setTotalMatches] = useState(0);

  return (
    <LogSearchContext.Provider
      value={{
        search,
        setSearch,
        position,
        setPosition,
        totalMatches: matches,
        setTotalMatches,
      }}
    >
      {children}
    </LogSearchContext.Provider>
  );
};

interface Props {
  onOpen: () => void;
}

export const LogSearch = ({ onOpen }: Props) => {
  const [active, setActive] = useState(false);
  const { search, setSearch, position, totalMatches, setPosition } = useContext(LogSearchContext);
  const inputRef = useRef<HTMLInputElement>(null);

  const open = useCallback(() => {
    setActive(true);
    onOpen();
  }, []);

  const close = useCallback(() => {
    setActive(false);
    setSearch("");
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Override the default browser search with our own.
      if ((event.ctrlKey || event.metaKey) && event.key === "f") {
        event.preventDefault();
        open();

        // If the input is already open, focus it.
        inputRef.current?.focus();
        inputRef.current?.select();
      }

      // Close search if it's currently open
      if (event.key === "Escape" && active) {
        // Stop propagation to prevent the event from bubbling up and doing things like closing drawers.
        event.stopPropagation();
        close();
      }
    },
    [active],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [active]);

  /**
   * Debounce the search input handler to mimimize re-renders when typing quickly.
   */
  const handleChange = useCallback(
    debounce(() => {
      setSearch(inputRef.current?.value || "");
    }, 50),
    [],
  );

  /**
   * Reset the position to the first match when the search changes.
   */
  useEffect(() => {
    if (totalMatches === 0) {
      setPosition(0);
    } else if (totalMatches > 0) {
      setPosition(1);
    }
  }, [totalMatches]);

  /**
   * Navigate to the next search result
   */
  const nextResult = useCallback(() => {
    if (!totalMatches) {
      return;
    }

    return setPosition(position === totalMatches ? 1 : position + 1);
  }, [totalMatches, position]);

  /**
   * Navigate to the previous search result
   */
  const prevResult = useCallback(() => {
    if (!totalMatches) {
      return;
    }

    return setPosition(position === 1 ? totalMatches : position - 1);
  }, [totalMatches, position]);

  /**
   * Keyboard navigation for the search results.
   */
  const handleEnter = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      // Navigate to the next match on enter.
      if (event.key === "Enter") {
        nextResult();
      }

      if (event.key === "Escape") {
        // Stop propagation to prevent the event from bubbling up and doing things like closing drawers.
        event.stopPropagation();
        close();
      }
    },
    [nextResult],
  );

  useEffect(() => {
    if (active) {
      inputRef.current?.focus();
    }
  }, [active]);

  return !active ? (
    <LogControl onClick={open}>
      <Icon icon="search" className="h-4 w-4" />
      Search
    </LogControl>
  ) : (
    <div
      data-active={!!search}
      className="group/search flex shrink-0 w-full py-1 text-[var(--theme-base01)] overflow-hidden"
    >
      <label className="px-1 py-0.5 flex gap-1 flex-auto items-center rounded-sm border border-transparent focus-within:border-current hover:border-[var(--theme-base02)] group-data-[active=true]:text-[var(--theme-base02)] focus-within:text-[var(--theme-base02)]">
        <Icon icon="search" className="h-4 w-4" />
        <input
          onKeyDown={handleEnter}
          ref={inputRef}
          type="text"
          name="search"
          onChange={handleChange}
          placeholder="Search"
          className="bg-transparent outline-none placeholder:text-current flex-auto"
        />

        <span className="text-xs tabular-nums text-[var(--theme-base01)]">
          {position}/{totalMatches}
        </span>
      </label>

      <div className="flex items-center gap-2 ml-2">
        <button
          title="Previous"
          onClick={prevResult}
          disabled={totalMatches === 0}
          className="flex items-center justify-center self-stretch w-5 hover:enabled:text-[var(--theme-base02)]"
        >
          <Icon icon="heroicons/outline/chevron-up" className="h-3 w-3 !m-0" />
        </button>
        <button
          title="Next"
          onClick={nextResult}
          disabled={totalMatches === 0}
          className="flex items-center justify-center self-stretch w-5 hover:enabled:text-[var(--theme-base02)]"
        >
          <Icon icon="heroicons/outline/chevron-down" className="h-3 w-3 !m-0" />
        </button>
        <button
          title="Close"
          onClick={close}
          className="flex items-center justify-center self-stretch w-5 hover:enabled:text-[var(--theme-base02)]"
        >
          <Icon icon="heroicons/20/solid/x-mark" className="h-4 w-4 !m-0" />
        </button>
      </div>
    </div>
  );
};
